import { Injectable, inject } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { CommonApiFuncService, StorageService, StorageType } from '@anddone/ui-utilities';
import { APIConstants } from '../constants/api.constant';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  loggedInUserData: any;
  public reportsDetailUpdate = new BehaviorSubject(false);
  currentReportUpdate = this.reportsDetailUpdate.asObservable();

  private storageService = inject(StorageService);
  private commonAPIFuncService = inject(CommonApiFuncService);

  getLoggedInData() {
    return JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}');
  }

  getSubscriptionReports() {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.getSubscriptionReports.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getSubscriptionReports success`)),
      catchError(this.commonAPIFuncService.handleError('getSubscriptionReports error/failed'))
    );
  }

  getOldSubscriptionReports() {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.getOldSubscriptionReports.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getSubscriptionReports success`)),
      catchError(this.commonAPIFuncService.handleError('getSubscriptionReports error/failed'))
    );
  }

  addReportSubscriptions(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.addReportSubscriptions.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.post(endpoint, reqObj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`addReportSubscriptions success`)),
      catchError(this.commonAPIFuncService.handleError('addReportSubscriptions error/failed'))
    );
  }

  addOldReportSubscriptions(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.addOldReportSubscriptions.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.post(endpoint, reqObj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`addReportSubscriptions success`)),
      catchError(this.commonAPIFuncService.handleError('addReportSubscriptions error/failed'))
    );
  }

  getAllOldReports(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let endpoint = APIConstants.reports.getAllOldReports.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    endpoint = `${endpoint}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getAllReports success`)),
      catchError(this.commonAPIFuncService.handleError('getAllReports error/failed'))
    );
  }

  getAllReports(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let endpoint = APIConstants.reports.getAllReports.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    endpoint = `${endpoint}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getAllReports success`)),
      catchError(this.commonAPIFuncService.handleError('getAllReports error/failed'))
    );
  }

  getSpecificReports(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let endpoint = APIConstants.reports.getSpecificReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.id);
    endpoint = `${endpoint}${this.commonAPIFuncService.buildQuery(reqObj.data)}`;
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getSpecificReports success`)),
      catchError(this.commonAPIFuncService.handleError('getSpecificReports error/failed'))
    );
  }

  getSpecificOldReports(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let endpoint = APIConstants.reports.getSpecificOldReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.id);
    endpoint = `${endpoint}${this.commonAPIFuncService.buildQuery(reqObj.data)}`;
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.get(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getSpecificReports success`)),
      catchError(this.commonAPIFuncService.handleError('getSpecificReports error/failed'))
    );
  }

  activateReport(id: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.activeDeactiveReportAction
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.post(endpoint, {}, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`activateReport success`)),
      catchError(this.commonAPIFuncService.handleError('activateReport error/failed'))
    );
  }

  activateOldReport(id: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.activeDeactiveOldReportAction
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.post(endpoint, {}, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`activateReport success`)),
      catchError(this.commonAPIFuncService.handleError('activateReport error/failed'))
    );
  }

  deactivateReport(id: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.activeDeactiveReportAction
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.delete(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`deactivateReport success`)),
      catchError(this.commonAPIFuncService.handleError('deactivateReport error/failed'))
    );
  }

  deactivateOldReport(id: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.activeDeactiveOldReportAction
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.delete(endpoint, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`deactivateReport success`)),
      catchError(this.commonAPIFuncService.handleError('deactivateReport error/failed'))
    );
  }

  downloadReport(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.downloadReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.reportId)
      .replace('{logId}', reqObj.logId);
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService.put(endpoint, {}, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`downloadReport success`)),
      catchError(this.commonAPIFuncService.handleError('downloadReport error/failed'))
    );
  }

  downloadOldReport(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.downloadOldReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.reportId)
      .replace('{logId}', reqObj.logId);
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.put(endpoint, {}, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`downloadReport success`)),
      catchError(this.commonAPIFuncService.handleError('downloadReport error/failed'))
    );
  }

  generateReport(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.getSpecificReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.id);
    const options = {
      customBaseUrl: environment.reportBaseUrlEKS,
      customHeaders: {
        'X-Version': '3.0',
      },
    };
    return this.commonAPIFuncService
      .post(endpoint, reqObj.data, options)
      .pipe()
      .pipe(
        tap((_a) => this.commonAPIFuncService.log(`generateReport success`)),
        catchError(this.commonAPIFuncService.handleError('generateReport error/failed'))
      );
  }

  generateOldReport(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const endpoint = APIConstants.reports.getSpecificOldReport
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{reportId}', reqObj.id);
    const options = {
      customBaseUrl: environment.reportBaseUrlLambda,
      customHeaders: {
        'X-Version': '2.3',
      },
    };
    return this.commonAPIFuncService.post(endpoint, reqObj.data, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`generateReport success`)),
      catchError(this.commonAPIFuncService.handleError('generateReport error/failed'))
    );
  }
}
